import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Column } from 'primereact/column';
import { PaginatorPageState } from 'primereact/paginator';
import {
	DataTable,
	DataTablePFSEvent,
	DataTableSortOrderType
} from 'primereact/datatable';
import { InputSwitch, InputSwitchChangeParams } from 'primereact/inputswitch';

import styles from './Users.module.scss';
import Filters from './filters/Filters';
import Pagination from '../common/pagination/Pagination';
import { DropdownOption } from '../../models/common/DropdownOption';
import { UsersSearchResponseModel } from '../../models/users/UsersSearchResponseModel';
import { UsersListingResponseModel } from '../../models/users/UsersListingResponseModel';
import { HttpService, IHttpService } from '../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../services/notifierService';
import { Routes } from '../../routes';
import { nameof } from '../../shared/utility';
import { useCookies } from 'react-cookie';
import { parseNumberFromString } from './utils/parseNumberFromString';

const Users = (): JSX.Element => {
	const location = useLocation();

	const currentPageFromLocation = parseNumberFromString(
		1,
		location.state?.currentPage
	);

	const [loading, setLoading] = useState<boolean>(false);
	const [first, setFirst] = useState<number>(
		currentPageFromLocation * 10 - 10
	);
	const [rows, setRows] = useState<number>(10);
	const [currentPage, setCurrentPage] = useState<number>(
		currentPageFromLocation
	);
	const [sortField, setSortField] = useState<string>('');
	const [sortOrder, setSortOrder] = useState<DataTableSortOrderType>();
	const [firstNameFilter, setFirstNameFilter] = useState<string>('');
	const [lastNameFilter, setLastNameFilter] = useState<string>('');
	const [usernameFilter, setUsernameFilter] = useState<string>('');
	const [divisionFilter, setDivisionFilter] = useState<string | number>('');
	const [securityRoleFilter, setSecurityRoleFilter] = useState<
		string | number
	>('');
	const [usersSearch, setUsersSearch] = useState<UsersSearchResponseModel>();
	const [usersListing, setUsersListing] = useState<
		UsersListingResponseModel[]
	>([]);
	const [shouldReload, setShouldReload] = useState<boolean>(false);

	const [divisionsOptions, setDivisionsOptions] = useState<DropdownOption[]>(
		[]
	);
	const [securityRolesOptions, setSecurityRolesOptions] = useState<
		DropdownOption[]
	>([]);

	const navigate = useNavigate();

	const [cookies, setCookie] = useCookies([
		'usersDivision',
		'usersSecurityRole'
	]);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const fetchUsers = () => {
		setLoading(true);

		let order = '';
		if (sortOrder) {
			order = sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : '';
		}
		httpService
			.get<UsersSearchResponseModel>(
				`users?page=${currentPage}&firstName=${firstNameFilter}&lastName=${lastNameFilter}&username=${usernameFilter}&divisionId=${divisionFilter}&securityRoleId=${securityRoleFilter}&sortBy=${sortField}&order=${order}`,
				undefined
			)
			.then((users) => {
				setUsersSearch(users);
				setUsersListing(users.models);
				setLoading(false);
			})
			.catch((err) => notifierService.error());
	};

	useEffect(() => {
		window.history.replaceState({}, '');
	}, []);

	useEffect(() => {
		fetchUsers();
	}, [
		httpService,
		notifierService,
		currentPage,
		sortField,
		sortOrder,
		firstNameFilter,
		lastNameFilter,
		usernameFilter,
		divisionFilter,
		securityRoleFilter
	]);

	useEffect(() => {
		if (shouldReload) {
			fetchUsers();
		}
	}, [shouldReload]);

	useEffect(() => {
		httpService
			.get<DropdownOption[]>('divisions/dropdown', undefined)
			.then((options) => {
				setDivisionsOptions(options);
				setDivisionFilter(
					cookies.usersDivision !== undefined
						? parseInt(cookies.usersDivision)
						: ''
				);
			})
			.catch((err) => notifierService.error());

		httpService
			.get<DropdownOption[]>('securityRoles/dropdown', undefined)
			.then((options) => {
				setSecurityRolesOptions(options);
				setSecurityRoleFilter(
					cookies.usersSecurityRole !== undefined
						? parseInt(cookies.usersSecurityRole)
						: ''
				);
			})
			.catch((err) => notifierService.error());
	}, [httpService, notifierService]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleInputSwitchChange = async (
		e: InputSwitchChangeParams,
		propName: keyof UsersListingResponseModel
	) => {
		const userId: number = +e.target.id;
		const userIndex: number = usersListing.findIndex(
			(u) => u.id === userId
		);

		const user = usersListing[userIndex];

		(user as any)[propName] = e.target.value;

		try {
			await httpService.put(
				`users/${userId}/changeActive`,
				undefined,
				undefined,
				false
			);

			let order = '';
			if (sortOrder) {
				order =
					sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : '';
			}

			const users = await httpService.get<UsersSearchResponseModel>(
				`users?page=${currentPage}&firstName=${firstNameFilter}&lastName=${lastNameFilter}&username=${usernameFilter}&divisionId=${divisionFilter}&securityRoleId=${securityRoleFilter}&sortBy=${sortField}&order=${order}`,
				undefined
			);

			setUsersListing(users.models);
		} catch {
			notifierService.error();
		}
	};

	const onRenderActive = useCallback(
		(cellData: UsersListingResponseModel) => {
			return (
				<InputSwitch
					id={cellData.id.toString()}
					checked={cellData.active}
					onChange={(e) => handleInputSwitchChange(e, 'active')}
				/>
			);
		},
		[handleInputSwitchChange]
	);

	const onRenderEditButton = (cellData: UsersListingResponseModel) => {
		return (
			<i
				className={`${styles.editIcon} pi pi-pencil`}
				onClick={() =>
					navigate(Routes.editUsers.getPath(cellData.id.toString()), {
						state: { currentPage: currentPage }
					})
				}
			/>
		);
	};

	const onPageChange = (event: PaginatorPageState) => {
		setFirst(event.first);
		setRows(event.rows);
		setCurrentPage(event.page! + 1);
	};

	const onSort = (event: DataTablePFSEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder);
	};

	return (
		<>
			<Filters
				divisionsOptions={divisionsOptions}
				securityRolesOptions={securityRolesOptions}
				setFirstNameFilter={setFirstNameFilter}
				setLastNameFilter={setLastNameFilter}
				setUsernameFilter={setUsernameFilter}
				divisionFilter={divisionFilter}
				setDivisionFilter={setDivisionFilter}
				securityRoleFilter={securityRoleFilter}
				setSecurityRoleFilter={setSecurityRoleFilter}
				setShouldReload={setShouldReload}
			/>

			<DataTable
				value={usersListing}
				responsiveLayout='scroll'
				sortField={sortField}
				sortOrder={sortOrder}
				onSort={onSort}
				loading={loading}
			>
				<Column
					style={{ width: '10%' }}
					sortable
					header='Status'
					body={onRenderActive}
					field={nameof<UsersListingResponseModel>('active')}
				/>

				<Column
					sortable
					header='First name'
					field={nameof<UsersListingResponseModel>('firstName')}
				/>

				<Column
					sortable
					header='Last name'
					field={nameof<UsersListingResponseModel>('lastName')}
				/>

				<Column
					sortable
					header='Username'
					field={nameof<UsersListingResponseModel>('username')}
				/>

				<Column
					sortable
					header='Security role'
					field={nameof<UsersListingResponseModel>(
						'securityRoleDescription'
					)}
				/>

				<Column
					sortable
					header='Division'
					field={nameof<UsersListingResponseModel>(
						'defaultDivisionDescription'
					)}
				/>

				<Column body={onRenderEditButton} />
			</DataTable>

			<Pagination
				totalRecords={usersSearch?.total}
				rows={rows}
				first={first}
				onPageChange={onPageChange}
			/>
		</>
	);
};

export default Users;
